import {SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {getNeighborHexKeys} from "../botGrid";
import {GARRISON_DOCTRINES} from "../../../configs/botGarrison";
import {BOT_ARCHETYPES, getArchetypeProfile} from "../strategy/archetypes";
import {HexMath} from "../../HexMath";
import {ConstructionAccessibility} from "../../ConstructionAccessibility";
import {QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN} from "../../../constants/construction";

// Волна 36, участок «Стройка» (C1–C5, боты золото+): чистые функции роста
// городов — без стора, для decideConstructionStep и юнит-тестов. Город здесь —
// по виртуальному состоянию цепочки (virtual.centralQuarters/allQuarters/hexes:
// реальные строения, pending этой фазы и шаги текущей цепочки), потому что
// citiesCache внутри фазы стройки не обновляется. По правилам базовый квартал
// всегда смежен своему центральному, а кварталы разных городов не ближе 3
// клеток друг к другу, поэтому город клетки определяется однозначно.

/** ЦК своего города среди соседей клетки (кандидат базового квартала) */
export const findW36AdjacentCentralQuarter = (adjacentKeys, centralQuarters) => {
  for (const adjKey of adjacentKeys) {
    if (centralQuarters.has(adjKey)) {
      return adjKey;
    }
  }
  return null;
};

/**
 * ЦК города, к кварталам которого примыкает клетка (кандидат фермы/казармы):
 * смежный ЦК или ЦК смежного базового квартала
 */
export const findW36CandidateCityCentralQuarter = (adjacentKeys, allQuarters, centralQuarters, gridRenderer) => {
  const adjacentCentralQuarter = findW36AdjacentCentralQuarter(adjacentKeys, centralQuarters);
  if (adjacentCentralQuarter) {
    return adjacentCentralQuarter;
  }
  for (const adjKey of adjacentKeys) {
    if (allQuarters.has(adjKey)) {
      const centralQuarter = findW36AdjacentCentralQuarter(getNeighborHexKeys(adjKey, gridRenderer), centralQuarters);
      if (centralQuarter) {
        return centralQuarter;
      }
    }
  }
  return null;
};

/** Базовые кварталы города: соседи его ЦК среди своих кварталов */
export const getW36CityBaseQuarters = (centralQuarterKey, allQuarters, centralQuarters, gridRenderer) => (
  getNeighborHexKeys(centralQuarterKey, gridRenderer)
    .filter((adjKey) => allQuarters.has(adjKey) && !centralQuarters.has(adjKey))
);

/** Число клеток с этим строением, смежных кварталам города (без дублей) */
export const countW36CityAdjacentTokens = (quarterKeys, token, hexes, gridRenderer) => {
  const cells = new Set();
  quarterKeys.forEach((quarterKey) => {
    getNeighborHexKeys(quarterKey, gridRenderer).forEach((adjKey) => {
      if (hexes[adjKey] === token) {
        cells.add(adjKey);
      }
    });
  });
  return cells.size;
};

/** Клетка свободна под квартал: пустая или с дорогой (квартал ставится поверх) */
export const checkIfW36FreeQuarterCell = (cellToken) => !cellToken || cellToken === TOKENS.ROAD;

// ---- C1: бронь кольца ЦК

/**
 * Множитель фермы/казармы на клетке кольца ЦК: бронь действует, пока в
 * городе меньше цели БК и клетка легальна под базовый квартал. Легальная
 * клетка есть — значит емкость кольца больше числа БК, и цель min(цель,
 * емкость) еще не взята; клетки, где квартал нельзя, не бронируются
 */
export const calcW36RingReserveFactor = ({baseQuartersCount, target, isLegalQuarterCell, factor}) => (
  baseQuartersCount < target && isLegalQuarterCell ? factor : 1
);

// ---- C2: ценность роста города

// Спецстроения, ради которых доктрина фракции растит город (configs/botGarrison):
// экономическая — доход (храм, гильдия, склад у ферм, лагерь у казарм),
// агрессивная — армия (лагерь, арсенал), смешанная — все
const DOCTRINE_SPECIALS = {
  [GARRISON_DOCTRINES.ECONOMIC]: [
    SPECIAL_BUILDING_TOKENS.TEMPLE, SPECIAL_BUILDING_TOKENS.GUILD,
    SPECIAL_BUILDING_TOKENS.WAREHOUSE, SPECIAL_BUILDING_TOKENS.MILITARY_CAMP,
  ],
  [GARRISON_DOCTRINES.AGGRESSIVE]: [
    SPECIAL_BUILDING_TOKENS.MILITARY_CAMP, SPECIAL_BUILDING_TOKENS.ARSENAL,
  ],
  [GARRISON_DOCTRINES.MIXED]: [
    SPECIAL_BUILDING_TOKENS.TEMPLE, SPECIAL_BUILDING_TOKENS.GUILD,
    SPECIAL_BUILDING_TOKENS.WAREHOUSE, SPECIAL_BUILDING_TOKENS.MILITARY_CAMP,
    SPECIAL_BUILDING_TOKENS.ARSENAL,
  ],
};

export const getW36DoctrineSpecialTokens = (doctrine) => (
  DOCTRINE_SPECIALS[doctrine] || DOCTRINE_SPECIALS[GARRISON_DOCTRINES.MIXED]
);

/**
 * Лучшая чистая ценность спецстроения на новом БК: max(0, ценность − цена ×
 * курс). options — [{value, priceCoins}] уже отобранных доктриной типов
 */
export const pickW36BestSpecialValue = (options, priceFactor) => options.reduce(
  (best, {value, priceCoins}) => Math.max(best, value - priceCoins * priceFactor), 0
);

/** Ценность роста города для базового квартала (0 — город уже на цели) */
export const calcW36QuarterGrowthValue = ({
  baseQuartersCount, target, bestSpecialValue, intensity, base, specialShare,
}) => (
  baseQuartersCount >= target ? 0 : (base + specialShare * bestSpecialValue) * intensity
);

// ---- C3: без «мусорных» казарм

/**
 * Казарма в город запрещена: кап города достигнут, либо это вторая (и далее)
 * казарма без лагеря/арсенала в городе и без свежего БК рядом под лагерь
 */
export const checkIfW36CityBarracksCapReached = ({
  cityBarracksCount, cap, requiresCampForSecond, hasCampOrArsenal, hasFreshCampPlatform,
}) => (
  cityBarracksCount >= cap
  || (requiresCampForSecond && cityBarracksCount >= 1 && !hasCampOrArsenal && !hasFreshCampPlatform)
);

// ---- C4: плотность своих городов

/**
 * Клетки в пределах правила дальности кварталов разных городов от клетки
 * (тот же обход, что у движка: calculateBlockedConstructionCellsForBaseQuarter,
 * горы не проходятся). Новый ЦК делает эти клетки нелегальными под базовые
 * кварталы других своих городов
 */
export const createW36QuarterRangeCells = () => {
  const accessibility = new ConstructionAccessibility(new HexMath(30, 'flat'));
  return (cellKey, hexes) => accessibility.findReachableCells(
    cellKey, QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN, hexes
  );
};

/**
 * Потери плотности от нового ЦК.
 *  - ownCities — [{baseQuartersCount, legalRingCells}] своих городов (легальные
 *    свободные клетки кольца под БК);
 *  - takenCells — клетки, которые новый ЦК делает нелегальными;
 *  - newCityCapacity — легальная емкость кольца самого нового ЦК.
 * lostSlots — сколько слотов до цели теряют свои города (лишние клетки сверх
 * цели не в счет), missingSlots — сколько слотов до цели не хватит новому городу
 */
export const calcW36CityDensityLoss = ({ownCities, takenCells, newCityCapacity, target}) => {
  let lostSlots = 0;
  ownCities.forEach(({baseQuartersCount, legalRingCells}) => {
    if (baseQuartersCount >= target) {
      return;
    }
    const before = Math.min(target, baseQuartersCount + legalRingCells.length);
    const after = Math.min(
      target, baseQuartersCount + legalRingCells.filter((cellKey) => !takenCells.has(cellKey)).length
    );
    lostSlots += before - after;
  });
  return {lostSlots, missingSlots: Math.max(0, target - newCityCapacity)};
};

// ---- C5: спецстроения по доктрине

export const checkIfW36AggressiveArchetype = (archetype) => archetype === BOT_ARCHETYPES.AGGRESSIVE_RUSH
  || archetype === BOT_ARCHETYPES.AGGRESSIVE_BUILDUP;

/**
 * military для гейтов спецстроений (гильдия без путей влияния, осторожность
 * направления к агрессорам) — архетипа БЕЗ бонусного множителя: у бота
 * золото+ множитель военных весов поднимал экономический архетип (0.8) выше
 * единицы, и гильдии без немедленных путей влияния не строились совсем.
 * Экономическая доктрина при смене архетипа на агрессивный (разовый RUSH на
 * слабого соседа, поздний BUILDUP) держит домашний архетип: храмы и гильдии
 * не гаснут из-за смены
 */
export const getW36GateMilitary = (archetype, homeArchetype, doctrine) => {
  const currentMilitary = getArchetypeProfile(archetype).military;
  if (doctrine !== GARRISON_DOCTRINES.ECONOMIC
    || !homeArchetype
    || !checkIfW36AggressiveArchetype(archetype)
    || checkIfW36AggressiveArchetype(homeArchetype)
  ) {
    return currentMilitary;
  }
  return Math.min(currentMilitary, getArchetypeProfile(homeArchetype).military);
};

/** Город фронтирный: квартал в maxDistance (BFS) от кварталов живых противников */
export const checkIfW36FrontierCity = (quarterKeys, enemyQuarterDistances, maxDistance) => (
  Boolean(enemyQuarterDistances)
  && quarterKeys.some((quarterKey) => (enemyQuarterDistances.get(quarterKey) ?? Infinity) <= maxDistance)
);
